import type TicketStore from 'customer-flows/stores/tickets'
import uid from 'customer-flows/utils/uid'

export default function tickets({ dispatch, stores, services }: CommandArgs) {
  const ticketStore: TicketStore = stores['tickets']

  const quoteBuilder = services['quote-builder']
  const eventTracker: EventTracker = services['event-tracker']

  function addTicket(ticketId: string, quantity = 1) {
    for (let i = 0; i < quantity; i++) {
      ticketStore.selectedTickets.push({
        resourceOptionId: ticketId,
        refid: uid(),
        reservationStatus: 'CONFIRMED'
      })
      ticketStore.selectedTickets = [...ticketStore.selectedTickets]
    }

    // each time ticket count changes, we need to check for per-booking questions that charge per guest
    // and adjust the number of items accordingly
    dispatch('custom-question:syncPerBookingChargesWithTicketCount')

    eventTracker.track('add_to_cart', { ticketId, quantity })
    eventTracker.trackOnce('Selected Ticket')
  }

  function cancelTicket(ticketId: string, quantity = 1) {
    for (let i = 0; i < quantity; i++) {
      const ticket = ticketStore.confirmedTickets.find(t => t.resourceOptionId === ticketId)
      if (ticket) {
        // as we might have registered answers for this ticket, we need to remove
        // local answers from memory
        dispatch('custom-question:removePerGuestQuestionAnswersForTicket', ticket.refid)
        ticket.reservationStatus = 'CANCELED'
      }
    }

    ticketStore.selectedTickets = [...ticketStore.selectedTickets]

    if (ticketStore.confirmedTickets.length === 0) {
      ticketStore.userHasManuallyEmptiedCart = true
    }

    eventTracker.track('remove_from_cart', { ticketId, quantity })

    // each time ticket count changes, we need to check for per-booking questions that charge per guest
    // and adjust the number of items accordingly
    dispatch('custom-question:syncPerBookingChargesWithTicketCount')
  }

  function cancelAllTickets({ updateQuote }: { updateQuote?: Boolean } = {}) {
    ticketStore.selectedTickets = ticketStore.confirmedTickets.map(ticket => {
      ticket.reservationStatus = 'CANCELED'
      return ticket
    })

    // Not debounced — clearing is discrete, so the fetch scope follows it at once.
    dispatch('availability:settleTicketQuantities')

    // each time ticket count changes, we need to check for per-booking questions that charge per guest
    // and adjust the number of items accordingly
    dispatch('custom-question:syncPerBookingChargesWithTicketCount')

    if (updateQuote) {
      dispatch('tickets:commitTickets')
    }
  }

  /**
   * Composes and commits the booking quote for the current ticket selection.
   *
   * STILL GATED ON AVAILABILITY by every caller below, and moving it ahead of that fetch does not
   * currently work — this is the thing to fix before the gate can come off:
   *
   * `availabilityTimeId` (a `strict` attr on the bookingQuotes model) resolves to
   * `validTimeslots[0]?.id`, and `validTimeslots` filters the selected date's slots by whether they
   * can fulfil the CURRENT ticket quantities. Immediately after a quantity change that filter is
   * the new quantities against the OLD slot data, so it fails closed and empties: the resolver
   * returns undefined, `_validateModel` rejects the compose, and `tryCommit` silently no-ops.
   * Nothing re-composes when availability lands, so the quote is never created at all — which the
   * booking flow's continue button reads as `missingQuote` and stays disabled forever.
   *
   * That failure is CLIENT-side, so "the BE rejects invalid commits" does not cover it — no request
   * is made. Un-gating needs the resolver to be able to answer optimistically (e.g. falling back to
   * `timeslotsForSelectedTime`, which is the same slot id without the quantity filter) or an
   * explicit re-commit on settle. Covered by `tests/acceptance/activity-flow/min-guest-test.js`.
   */
  async function commitTickets() {
    quoteBuilder.include('tickets', 'availabilityTimeId').compose('bookingQuotes').tryCommit()
  }

  /**
   * Command: Apply a positive ticket quantity change and reconcile booking state.
   *
   * This function represents a transactional boundary for increasing the quantity
   * of a selected ticket. It performs the following operations in order:
   *
   * 1. Mutates ticket state by adding the specified quantity.
   * 2. Awaits an availability refresh for the new quantities (the engine also fetches the
   *    selected-date times family reactively off them).
   * 3. Rebuilds and attempts to commit booking quotes to reflect the new selection.
   *
   * Step 2 gates step 3 — see `commitTickets` for why the commit cannot yet be moved ahead of it.
   *
   * This is NOT a pure mutation. It intentionally orchestrates state mutation,
   * availability recalculation, and pricing recomposition as a single workflow.
   *
   * @param ticketId - The unique identifier of the ticket to increment.
   * @param quantity - The amount to increase the ticket quantity by. Defaults to 1.
   */
  async function applyTicketIncrement(ticketId: string, quantity = 1) {
    dispatch('tickets:addTicket', ticketId, quantity)

    // Not debounced — settle now, or the ensure below refreshes the previous quantities.
    dispatch('availability:settleTicketQuantities')

    // we look for availability for all dates visible
    // this will also look for selected date's times (the engine fetches the selected-date times
    // family reactively off the current ticket quantities)
    await dispatch('availability:ensureAvailabilityDates')

    dispatch('tickets:commitTickets')
  }

  /**
   * Command: Apply a negative ticket quantity change and reconcile booking state.
   *
   * This function represents a transactional boundary for decreasing the quantity
   * of a selected ticket. It performs the following operations in order:
   *
   * 1. Mutates ticket state by cancelling the specified quantity.
   * 2. Awaits an availability refresh for the new quantities.
   * 3. Rebuilds and attempts to commit booking quotes to reflect the updated selection.
   *
   * Step 2 gates step 3 — see `commitTickets` for why the commit cannot yet be moved ahead of it.
   *
   * This is NOT a pure mutation. It orchestrates mutation, availability recalculation,
   * and pricing recomposition as a single workflow.
   *
   * @param ticketId - The unique identifier of the ticket to decrement.
   * @param quantity - The amount to decrease the ticket quantity by. Defaults to 1.
   */
  async function applyTicketDecrement(ticketId: string, quantity = 1) {
    dispatch('tickets:cancelTicket', ticketId, quantity)

    // Not debounced — see `applyTicketIncrement`.
    dispatch('availability:settleTicketQuantities')

    await dispatch('availability:ensureAvailabilityDates')

    dispatch('tickets:commitTickets')
  }

  /**
   * Command: debounced tail of a ticket-quantity change.
   *
   * The ticket-picker mutates the real ticket count immediately (via `addTicket`/`cancelTicket`)
   * on every click, so the display reacts to the current quantities right away. This method is the
   * remainder of the old `applyTicketIncrement`/`applyTicketDecrement` workflow — settling those
   * quantities into the fetch scope, refreshing availability for them, then committing the quote —
   * and is injected with `{ debounce: 300, restartable: true }` so a streak of clicks coalesces
   * into a single refresh + commit instead of one per click. The settle must lead, or a click can
   * re-key the fetch scope and escape the debounce.
   *
   * It resolves only after all three, which the ticket-picker relies on: it hangs `updateFlow` off
   * this promise, and step validity needs authoritative slots (see `commitTickets` for the gate).
   */
  async function commitTicketQuantityChange() {
    dispatch('availability:settleTicketQuantities')

    await dispatch('availability:ensureAvailabilityDates')

    dispatch('tickets:commitTickets')
  }

  /**
   * Add minimum required tickets if any are below the minimum.
   */
  async function addMinTicketsIfRequired() {
    for (const ticket of ticketStore.whiteListedTickets) {
      const min = ticketStore.getMinQuantityForTicket(ticket.id)
      const isMinRequired = ticketStore.isMinRequiredForTicket(ticket.id)
      const count = ticketStore.getCountForTicket(ticket.id)

      if (isMinRequired && count < min) {
        const amountToAdd = min - count
        await dispatch('tickets:applyTicketIncrement', ticket.id, amountToAdd)
      }
    }
  }

  function preselectDefaultTicket() {
    if (!ticketStore.canAutoAddTicket) return

    const ticket = ticketStore.selectableTickets[0]
    if (!ticket) return

    const min = ticketStore.getMinQuantityForTicket(ticket.id) || 1
    addTicket(ticket.id, min)
  }

  /**
   * Pre-selects all tickets with `isMinRequired: true` to their minimum quantity
   * before the initial availability fetch, so they are included in the availability
   * payload from the start. Intended for flows where the ticket picker is visible.
   */
  function preselectRequiredTickets() {
    for (const ticket of ticketStore.selectableTickets) {
      if (!ticketStore.isMinRequiredForTicket(ticket.id)) continue

      const min = ticketStore.getMinQuantityForTicket(ticket.id)
      if (!min) continue

      const currentCount = ticketStore.getCountForTicket(ticket.id)
      if (currentCount >= min) continue

      addTicket(ticket.id, min - currentCount)
    }
  }

  return {
    addTicket,
    cancelTicket,
    cancelAllTickets,
    addMinTicketsIfRequired,
    applyTicketIncrement,
    applyTicketDecrement,
    commitTicketQuantityChange,
    preselectDefaultTicket,
    preselectRequiredTickets,
    commitTickets
  }
}

declare global {
  export type TicketsCommands = ReturnType<typeof tickets>

  // Add to global scope
  interface CommandRegistry {
    tickets: TicketsCommands
  }
}
